/**
 * The sky over an aerodrome seen from the side: a METAR's cloud groups turned
 * into bands a chart can draw at their heights.
 *
 * `FEW032 BKN040 OVC060` is three facts a pilot reads in sequence and has to
 * stack in their head. Drawn on an altitude axis they read at a glance — where
 * the first layer is, where the ceiling is, how much room there is between the
 * layers — which is what the owner asked for (2026-10-17).
 *
 * ## What this model refuses to do
 *
 *  - **A layer whose base was not measured is never placed.** `BKN///` says
 *    there is a broken layer and the sensor could not tell its height. Drawing
 *    it anywhere is inventing a ceiling (rule 2). It is listed apart, as
 *    "base unknown".
 *  - **A METAR states no tops.** Every band is a base and a nominal depth for
 *    the glyph, never a thickness: the chart says the base and nothing about
 *    how deep the cloud is.
 *  - **"No cloud" is a statement, not an empty list.** CAVOK, NSC, NCD, SKC and
 *    CLR each say something different, and a report with no cloud group at all
 *    says nothing. Each is its own state, so the screen can say which.
 *  - **No freezing level.** A METAR does not carry one, and an estimate from
 *    the surface temperature and a standard lapse rate is a number with no
 *    source. It is not drawn.
 *
 * Pure: no Vue, no dates.
 */

import type { CloudAmount, CloudLayer } from './types'

/** The amounts that are a layer at a height, as opposed to a statement about the whole sky. */
export type LayerAmount = Extract<CloudAmount, 'FEW' | 'SCT' | 'BKN' | 'OVC' | 'VV'>

/** The clear-sky groups, and CAVOK, which says the same thing about cloud with more besides. */
export type ClearSkyCode = 'CAVOK' | 'NSC' | 'NCD' | 'SKC' | 'CLR'

/**
 * Oktas each amount covers — ICAO Annex 3, App. 3, 4.5.4.3 (FEW 1–2, SCT 3–4,
 * BKN 5–7, OVC 8). Vertical visibility is not an amount: the sky is obscured,
 * and it is drawn as such rather than as eight oktas of cloud.
 */
export const OKTAS: Record<Exclude<LayerAmount, 'VV'>, { min: number; max: number }> = {
	FEW: { min: 1, max: 2 },
	SCT: { min: 3, max: 4 },
	BKN: { min: 5, max: 7 },
	OVC: { min: 8, max: 8 },
}

/**
 * How many of the chart's eight cloud slots are filled for an amount: the
 * upper end of FEW and SCT, the middle of BKN. The glyph shows the order of
 * magnitude — it is not a claim to the okta, which the report does not make.
 */
export const DRAWN_OKTAS: Record<Exclude<LayerAmount, 'VV'>, number> = {
	FEW: 2,
	SCT: 4,
	BKN: 6,
	OVC: 8,
}

/**
 * The height below which each clear-sky code says there is no cloud, ft AGL,
 * or null where it gives none.
 *
 *  - CAVOK, NSC: no cloud below 5000 ft or the highest minimum sector altitude,
 *    whichever is greater, and no CB or TCU (Annex 3, App. 3, 2.2 and 4.5.4.4).
 *    5000 ft is the half the app can state; the MSA half is said in words.
 *  - CLR: no cloud below 12,000 ft — an automated station's ceilometer range
 *    (the FAA METAR key, AIM chapter 7).
 *  - NCD (no cloud detected by an automatic system) and SKC (sky clear) give no
 *    height; NCD in particular is bounded by a sensor whose range the report
 *    does not state, so none is invented.
 */
export const CLEAR_BELOW_FT: Record<ClearSkyCode, number | null> = {
	CAVOK: 5_000,
	NSC: 5_000,
	CLR: 12_000,
	NCD: null,
	SKC: null,
}

export interface CloudBand {
	amount: LayerAmount
	/** Base above aerodrome elevation, ft — as the METAR reports it. */
	baseAglFt: number
	/** The same base above mean sea level, when the field's elevation is known. */
	baseAmslFt: number | null
	/** Eight-slot density for the glyph; 8 for vertical visibility (the whole sky obscured). */
	drawnOktas: number
	convective: 'CB' | 'TCU' | null
	/** The lowest BKN, OVC or VV with a height: what a ceiling is. */
	isCeiling: boolean
	/** `BKN040`: the group as it appeared, for the label beside the band. */
	group: string
}

/** A layer whose base is `///`: reported, and never placed. */
export interface UnplacedLayer {
	amount: LayerAmount
	convective: 'CB' | 'TCU' | null
	group: string
	/** A BKN, OVC or VV with no height: a ceiling exists and its height is not known. */
	ceilingClass: boolean
}

export type CloudProfile =
	| {
			kind: 'layers'
			/** Lowest first. */
			bands: CloudBand[]
			unplaced: UnplacedLayer[]
			/** The ceiling base, ft AGL, or null when none was measured. */
			ceilingAglFt: number | null
			/** A ceiling-class layer was reported without a height. */
			ceilingUnknown: boolean
			/** The top of the chart's axis, ft AGL. */
			axisTopFt: number
			/** Tick heights, ft AGL, from 0 up. */
			ticksFt: number[]
	  }
	| {
			kind: 'clear'
			code: ClearSkyCode
			/** No cloud below this, ft AGL — null when the code states no height. */
			clearBelowFt: number | null
			axisTopFt: number
			ticksFt: number[]
	  }
	| {
			/** The report has no cloud group at all: it says nothing about the sky. */
			kind: 'notReported'
	  }

/** The axis shows at least this much sky — a FEW008 alone must not fill the chart edge to edge. */
const MIN_AXIS_FT = 5_000
/** Room above the highest base for its glyph and label. */
const HEADROOM_FT = 1_500

function isLayerAmount(amount: CloudAmount): amount is LayerAmount {
	return amount === 'FEW' || amount === 'SCT' || amount === 'BKN' || amount === 'OVC' || amount === 'VV'
}

function isCeilingClass(amount: LayerAmount): boolean {
	return amount === 'BKN' || amount === 'OVC' || amount === 'VV'
}

/** `BKN040CB`, `VV///`: the group as a METAR writes it. */
export function cloudGroup(layer: Pick<CloudLayer, 'amount' | 'baseAglFt' | 'convective'>): string {
	const height =
		layer.baseAglFt === null ? '///' : String(Math.round(layer.baseAglFt / 100)).padStart(3, '0')
	return `${layer.amount}${height}${layer.convective ?? ''}`
}

/**
 * The step between axis ticks for a given top: about four to six ticks,
 * always a round number a pilot reads without arithmetic.
 */
export function tickStepFt(axisTopFt: number): number {
	if (axisTopFt <= 6_000) return 1_000
	if (axisTopFt <= 12_000) return 2_000
	if (axisTopFt <= 30_000) return 5_000
	return 10_000
}

/** The axis for a highest height: headroom, a floor, and a whole step on top. */
export function axisFor(highestFt: number): { axisTopFt: number; ticksFt: number[] } {
	const wanted = Math.max(MIN_AXIS_FT, highestFt + HEADROOM_FT)
	const step = tickStepFt(wanted)
	const axisTopFt = Math.ceil(wanted / step) * step
	const ticksFt: number[] = []
	for (let ft = 0; ft <= axisTopFt; ft += step) ticksFt.push(ft)
	return { axisTopFt, ticksFt }
}

export interface CloudProfileInput {
	clouds: readonly CloudLayer[]
	/** CAVOK was reported: a positive statement about cloud. */
	cavok: boolean
}

/**
 * The profile for one report — a METAR, or a TAF group, which carry the same
 * cloud fields. `elevationFt` is the aerodrome's, from the database or the
 * pilot's card; null leaves the AMSL heights absent rather than equal to AGL.
 */
export function buildCloudProfile(report: CloudProfileInput, elevationFt: number | null): CloudProfile {
	const layers = report.clouds.filter((layer): layer is CloudLayer & { amount: LayerAmount } =>
		isLayerAmount(layer.amount),
	)

	if (!layers.length) {
		const code: ClearSkyCode | null = report.cavok
			? 'CAVOK'
			: ((report.clouds.find((layer) => !isLayerAmount(layer.amount))?.amount as
					ClearSkyCode | undefined) ?? null)
		if (!code) return { kind: 'notReported' }
		const clearBelowFt = CLEAR_BELOW_FT[code]
		return { kind: 'clear', code, clearBelowFt, ...axisFor(clearBelowFt ?? 0) }
	}

	const placed = layers
		.filter((layer) => layer.baseAglFt !== null)
		.sort((a, b) => a.baseAglFt! - b.baseAglFt!)
	const ceilingLayer = placed.find((layer) => isCeilingClass(layer.amount)) ?? null
	const bands: CloudBand[] = placed.map((layer) => ({
		amount: layer.amount,
		baseAglFt: layer.baseAglFt!,
		baseAmslFt: elevationFt === null ? null : layer.baseAglFt! + Math.round(elevationFt),
		drawnOktas: layer.amount === 'VV' ? 8 : DRAWN_OKTAS[layer.amount],
		convective: layer.convective ?? null,
		isCeiling: layer === ceilingLayer,
		group: cloudGroup(layer),
	}))
	const unplaced: UnplacedLayer[] = layers
		.filter((layer) => layer.baseAglFt === null)
		.map((layer) => ({
			amount: layer.amount,
			convective: layer.convective ?? null,
			group: cloudGroup(layer),
			ceilingClass: isCeilingClass(layer.amount),
		}))
	const highest = bands.length ? bands[bands.length - 1]!.baseAglFt : 0
	return {
		kind: 'layers',
		bands,
		unplaced,
		ceilingAglFt: ceilingLayer?.baseAglFt ?? null,
		ceilingUnknown: unplaced.some((layer) => layer.ceilingClass),
		...axisFor(highest),
	}
}
